import { useCallback, useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'
import type { PostgrestError } from '@supabase/supabase-js'
import { Archive, CheckCircle2, Eye, EyeOff, Plus, RefreshCw, Save, Star } from 'lucide-react'
import {
  ceramicPackagePayload,
  fetchCeramicPackageRows,
  mergeEditableCeramicPackages,
  type EditableCeramicPackage,
} from '../../lib/ceramic-packages'
import { fetchServiceRows, mergeEditableServices, classifyAdminServiceGroup } from '../../lib/service-catalog'
import type { EditableService } from '../../lib/service-catalog'
import { supabase } from '../../lib/supabase'
import { AdminSidebar } from '../admin/admin-sidebar'
import { PageTitle } from '../seo/page-title'
import { Button } from '../ui/button'

type LoadStatus = 'loading' | 'ready' | 'error'

const blankService: EditableService = {
  slug: '',
  name: '',
  description: '',
  base_price: '',
  duration_minutes: '120',
  order_index: 0,
  active: true,
  archived_at: null,
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function servicePayload(service: EditableService, orderIndex: number) {
  const price = Number(service.base_price)
  const duration = Number(service.duration_minutes)

  if (!service.name.trim()) throw new Error('Service name is required.')
  if (!service.slug.trim()) throw new Error('Service slug is required.')
  if (!Number.isFinite(price) || price < 0) throw new Error('Enter a valid starting price.')
  if (!Number.isFinite(duration) || duration <= 0) throw new Error('Enter a valid duration.')

  return {
    slug: slugify(service.slug),
    name: service.name.trim(),
    description: service.description.trim(),
    base_price: price,
    price_cents: Math.round(price * 100),
    duration_minutes: duration,
    base_duration_minutes: duration,
    buffer_before_minutes: 0,
    buffer_after_minutes: 15,
    order_index: orderIndex,
    active: service.active,
    archived_at: null,
  }
}

export function AdminServices() {
  const [services, setServices] = useState<EditableService[]>([])
  const [newService, setNewService] = useState<EditableService>(blankService)
  const [ceramicPackageItems, setCeramicPackageItems] = useState<EditableCeramicPackage[]>([])
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [packageStatus, setPackageStatus] = useState<LoadStatus>('loading')
  const [message, setMessage] = useState<string | null>(null)
  const [savingSlug, setSavingSlug] = useState<string | null>(null)
  const [savingPackageId, setSavingPackageId] = useState<string | null>(null)

  const loadServices = useCallback(async () => {
    setStatus('loading')
    setMessage(null)

    try {
      const rows = await fetchServiceRows({ includeInactive: true, includeArchived: true })
      setServices(mergeEditableServices(rows))
      setStatus('ready')
    } catch (error) {
      const serviceError = error as PostgrestError
      setMessage(serviceError.message || 'Unable to load services from Supabase.')
      setStatus('error')
    }
  }, [])

  const loadCeramicPackages = useCallback(async () => {
    setPackageStatus('loading')
    setMessage(null)

    try {
      const rows = await fetchCeramicPackageRows({ includeInactive: true })
      setCeramicPackageItems(mergeEditableCeramicPackages(rows))
      setPackageStatus('ready')
    } catch (error) {
      const packageError = error as PostgrestError
      setCeramicPackageItems(mergeEditableCeramicPackages())
      setMessage(packageError.message || 'Unable to load ceramic packages from Supabase.')
      setPackageStatus('error')
    }
  }, [])

  useEffect(() => {
    void loadServices()
    void loadCeramicPackages()
  }, [loadCeramicPackages, loadServices])

  function handleServiceChange(slug: string, field: keyof EditableService, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const value = field === 'active' && event.target instanceof HTMLInputElement ? event.target.checked : event.target.value
    setServices((current) =>
      current.map((service) => (service.slug === slug ? { ...service, [field]: value } : service)),
    )
  }

  function handleNewServiceChange(field: keyof EditableService, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const value = field === 'active' && event.target instanceof HTMLInputElement ? event.target.checked : event.target.value
    setNewService((current) => {
      const next = { ...current, [field]: value }
      if (field === 'name' && !current.slug) next.slug = slugify(String(value))
      return next
    })
  }

  function handlePackageChange(
    id: string,
    field: keyof EditableCeramicPackage,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const value = event.target instanceof HTMLInputElement && event.target.type === 'checkbox'
      ? event.target.checked
      : field === 'sortOrder'
        ? Number(event.target.value)
      : event.target.value

    setCeramicPackageItems((current) =>
      current.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    )
  }

  async function saveCeramicPackage(item: EditableCeramicPackage, orderIndex: number) {
    try {
      setSavingPackageId(item.id)
      setMessage(null)

      if (item.featured) {
        const { error: resetError } = await supabase
          .from('ceramic_packages')
          .update({ featured: false })
          .neq('id', item.id)

        if (resetError) throw resetError
      }

      const { error } = await supabase
        .from('ceramic_packages')
        .upsert(ceramicPackagePayload(item, item.sortOrder || (orderIndex + 1) * 10), { onConflict: 'id' })

      if (error) throw error

      setMessage(`${item.displayName} saved. Public carousel and booking prices will use this on next load.`)
      await loadCeramicPackages()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save ceramic package.')
    } finally {
      setSavingPackageId(null)
    }
  }

  async function setPrimaryPackage(item: EditableCeramicPackage) {
    try {
      setSavingPackageId(item.id)
      setMessage(null)

      const { error: resetError } = await supabase
        .from('ceramic_packages')
        .update({ featured: false })
        .neq('id', item.id)

      if (resetError) throw resetError

      const { error } = await supabase
        .from('ceramic_packages')
        .upsert(ceramicPackagePayload({ ...item, featured: true, active: true }, item.sortOrder), { onConflict: 'id' })

      if (error) throw error

      setMessage(`${item.displayName} is now the primary carousel package.`)
      await loadCeramicPackages()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to set primary package.')
    } finally {
      setSavingPackageId(null)
    }
  }

  async function saveService(service: EditableService, orderIndex: number) {
    try {
      setSavingSlug(service.slug)
      setMessage(null)

      const { error } = await supabase.from('services').upsert(servicePayload(service, orderIndex), { onConflict: 'slug' })

      if (error) throw error

      setMessage(`${service.name} saved. Public pages will use this service on next load.`)
      await loadServices()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save service.')
    } finally {
      setSavingSlug(null)
    }
  }

  async function createService() {
    await saveService({ ...newService, slug: slugify(newService.slug || newService.name) }, services.length)
    setNewService(blankService)
  }

  async function setServiceVisibility(service: EditableService, active: boolean) {
    setSavingSlug(service.slug)
    setMessage(null)

    const { error } = await supabase
      .from('services')
      .update({ active, archived_at: null })
      .eq('slug', service.slug)

    setSavingSlug(null)

    if (error) {
      setMessage(error.message)
      return
    }

    setMessage(active ? `${service.name} is visible on public service lists.` : `${service.name} is hidden from public service lists.`)
    await loadServices()
  }

  async function archiveService(service: EditableService) {
    setSavingSlug(service.slug)
    setMessage(null)

    const { error } = await supabase
      .from('services')
      .update({ active: false, archived_at: new Date().toISOString() })
      .eq('slug', service.slug)

    setSavingSlug(null)

    if (error) {
      setMessage(error.message)
      return
    }

    setMessage(`${service.name} archived and removed from public service lists.`)
    await loadServices()
  }

  const tailoredServices = services.filter((service) => classifyAdminServiceGroup(service) === 'tailored')
  const legacyServices = services.filter((service) => classifyAdminServiceGroup(service) === 'legacy')

  function renderServiceCard(service: EditableService, index: number) {
    const archived = Boolean(service.archived_at)
    return (
      <article className="service-editor-card" key={service.slug}>
        <div>
          <span>{service.slug}</span>
          {archived ? <Archive size={18} /> : service.active ? <CheckCircle2 size={18} /> : <EyeOff size={18} />}
        </div>
        <p className={service.active && !archived ? 'service-visibility visible' : 'service-visibility hidden'}>
          {archived ? 'Legacy / Archived' : service.active ? 'Active' : 'Hidden from public lists'}
        </p>
        <label>
          Service name
          <input value={service.name} onChange={(event) => handleServiceChange(service.slug, 'name', event)} />
        </label>
        <label>
          Slug
          <input value={service.slug} onChange={(event) => handleServiceChange(service.slug, 'slug', event)} />
        </label>
        <label>
          Starting price
          <input inputMode="decimal" min="0" type="number" value={service.base_price} onChange={(event) => handleServiceChange(service.slug, 'base_price', event)} />
        </label>
        <label>
          Duration minutes
          <input inputMode="numeric" min="1" type="number" value={service.duration_minutes} onChange={(event) => handleServiceChange(service.slug, 'duration_minutes', event)} />
        </label>
        <label>
          Public description
          <textarea value={service.description} rows={4} onChange={(event) => handleServiceChange(service.slug, 'description', event)} />
        </label>
        <div className="service-card-actions">
          <Button type="button" onClick={() => void saveService(service, service.order_index)} disabled={savingSlug === service.slug}>
            <Save size={16} /> {savingSlug === service.slug ? 'Saving...' : 'Save'}
          </Button>
          <button
            type="button"
            className="visibility-action"
            onClick={() => void setServiceVisibility(service, !service.active)}
            disabled={savingSlug === service.slug}
          >
            {service.active ? <EyeOff size={16} /> : <Eye size={16} />}
            {service.active ? 'Hide' : 'Show'}
          </button>
          {archived ? null : (
            <button type="button" onClick={() => void archiveService(service)} disabled={savingSlug === service.slug}>
              <Archive size={16} /> Archive
            </button>
          )}
        </div>
      </article>
    )
  }

  return (
    <div className="admin-dashboard">
      <PageTitle title="Services | AUTOGLOW Detailing Admin" />
      <AdminSidebar active="services" />
      <main>
        <header className="admin-topbar">
          <div>
            <p className="eyebrow">Service Catalog</p>
            <h1>Services</h1>
          </div>
          <div className="admin-topbar-actions">
            <button type="button" onClick={() => void loadServices()} disabled={status === 'loading'}>
              <RefreshCw size={17} /> {status === 'loading' ? 'Refreshing' : 'Refresh'}
            </button>
            <a href="/">View Homepage</a>
          </div>
        </header>

        {message ? <p className={status === 'error' || packageStatus === 'error' ? 'admin-alert error' : 'admin-alert'}>{message}</p> : null}

        <section className="admin-card service-pricing-card">
          <div className="admin-card-heading">
            <div>
              <h2>Ceramic Protection</h2>
              <p>
                1 Year CanCoat EVO, 3 Year Flash EVO and 5 Year Infinite EVO. This catalog is the
                public booking source of truth. Do not duplicate these as `services` rows.
              </p>
            </div>
          </div>
          {packageStatus === 'error' ? (
            <p className="admin-alert error">Showing fallback package data. Apply the ceramic package migration before saving package edits.</p>
          ) : null}
          <div className="service-editor-grid">
            {ceramicPackageItems.map((item, index) => (
              <article className={item.featured ? 'service-editor-card ceramic-editor-card primary' : 'service-editor-card ceramic-editor-card'} key={item.id}>
                <div>
                  <span>{item.slug}</span>
                  {item.featured ? <Star size={18} fill="currentColor" /> : item.active ? <CheckCircle2 size={18} /> : <EyeOff size={18} />}
                </div>
                <p className={item.active ? 'service-visibility visible' : 'service-visibility hidden'}>
                  {item.featured ? 'Primary carousel package' : item.active ? 'Visible in carousel' : 'Hidden from carousel'}
                </p>
                <label>
                  Display name
                  <input value={item.displayName} onChange={(event) => handlePackageChange(item.id, 'displayName', event)} />
                </label>
                <label>
                  Slug
                  <input value={item.slug} onChange={(event) => handlePackageChange(item.id, 'slug', event)} />
                </label>
                <label>
                  GYEON product
                  <input value={item.productName} onChange={(event) => handlePackageChange(item.id, 'productName', event)} />
                </label>
                <label>
                  Badge
                  <input value={item.badge} onChange={(event) => handlePackageChange(item.id, 'badge', event)} />
                </label>
                <label>
                  Starting price
                  <input inputMode="decimal" min="0" type="number" value={item.startingPrice} onChange={(event) => handlePackageChange(item.id, 'startingPrice', event)} />
                </label>
                <label>
                  Durability
                  <input value={item.durabilityLabel} onChange={(event) => handlePackageChange(item.id, 'durabilityLabel', event)} />
                </label>
                <label>
                  Sort order
                  <input inputMode="numeric" min="0" type="number" value={item.sortOrder} onChange={(event) => handlePackageChange(item.id, 'sortOrder', event)} />
                </label>
                <label>
                  Warranty label
                  <input value={item.warrantyLabel} onChange={(event) => handlePackageChange(item.id, 'warrantyLabel', event)} />
                </label>
                <label className="wide">
                  Optional upgrade
                  <input value={item.optionalUpgrade} onChange={(event) => handlePackageChange(item.id, 'optionalUpgrade', event)} />
                </label>
                <label className="wide">
                  Description
                  <textarea value={item.description} rows={4} onChange={(event) => handlePackageChange(item.id, 'description', event)} />
                </label>
                <label className="service-checkbox-row">
                  <input type="checkbox" checked={item.active} onChange={(event) => handlePackageChange(item.id, 'active', event)} />
                  Visible in carousel and booking flow
                </label>
                <label className="service-checkbox-row">
                  <input type="checkbox" checked={item.warrantyEligible} onChange={(event) => handlePackageChange(item.id, 'warrantyEligible', event)} />
                  Warranty eligible
                </label>
                <div className="service-card-actions">
                  <Button type="button" onClick={() => void saveCeramicPackage(item, index)} disabled={savingPackageId === item.id}>
                    <Save size={16} /> {savingPackageId === item.id ? 'Saving...' : 'Save'}
                  </Button>
                  <button
                    type="button"
                    className="visibility-action"
                    onClick={() => void setPrimaryPackage(item)}
                    disabled={savingPackageId === item.id || item.featured}
                  >
                    <Star size={16} />
                    {item.featured ? 'Primary' : 'Set Primary'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="admin-card service-pricing-card">
          <div className="admin-card-heading">
            <div>
              <h2>Add Service</h2>
              <p>Optional. New rows here do not drive the ceramic booking funnel.</p>
            </div>
          </div>
          <div className="service-create-grid">
            <label>
              Service name
              <input value={newService.name} onChange={(event) => handleNewServiceChange('name', event)} placeholder="Maintenance Detail" />
            </label>
            <label>
              Slug
              <input value={newService.slug} onChange={(event) => handleNewServiceChange('slug', event)} placeholder="maintenance-detail" />
            </label>
            <label>
              Starting price
              <input inputMode="decimal" min="0" type="number" value={newService.base_price} onChange={(event) => handleNewServiceChange('base_price', event)} />
            </label>
            <label>
              Duration minutes
              <input inputMode="numeric" min="1" type="number" value={newService.duration_minutes} onChange={(event) => handleNewServiceChange('duration_minutes', event)} />
            </label>
            <label className="wide">
              Public description
              <textarea value={newService.description} rows={3} onChange={(event) => handleNewServiceChange('description', event)} />
            </label>
            <Button type="button" onClick={() => void createService()}>
              <Plus size={16} /> Add Service
            </Button>
          </div>
        </section>

        <section className="admin-card service-pricing-card">
          <div className="admin-card-heading">
            <div>
              <h2>Tailored Services</h2>
              <p>
                Paint Correction and detailing-related `services` rows. Public ceramic booking does
                not use this list. Historic bookings can still reference these slugs.
              </p>
            </div>
          </div>
          <div className="service-editor-grid">
            {tailoredServices.length === 0 ? <p className="admin-empty-state">No active tailored service rows.</p> : null}
            {tailoredServices.map((service) => renderServiceCard(service, service.order_index))}
          </div>
        </section>

        <section className="admin-card service-pricing-card">
          <div className="admin-card-heading">
            <div>
              <h2>Legacy records</h2>
              <p>
                Hidden, archived, or retired fixed detailing rows (including old ceramic-coating
                package rows). Do not delete them if they may still be linked to historic bookings.
              </p>
            </div>
          </div>
          <div className="service-editor-grid">
            {legacyServices.length === 0 ? <p className="admin-empty-state">No legacy service rows.</p> : null}
            {legacyServices.map((service) => renderServiceCard(service, service.order_index))}
          </div>
        </section>
      </main>
    </div>
  )
}

export default AdminServices
